import type { TaxLineType as RuntimeTaxLineType } from './tax';
import type { UpliftBasis } from '@/lib/sales/markup-margin';
import type { PricingTreatment, ServiceSurcharge } from '@/lib/sales/service-rollup';

/**
 * NOT-590 — there is no `package` type: a service that comes with parts is a
 * `service` carrying `components`, not a container. A `bundle` is the separate
 * thing — several items sold together as one customer-facing line.
 */
export type ItemType =
  | 'service'
  | 'material'
  | 'equipment'
  | 'membership'
  /** Several items sold together for one price, shown as a single line. */
  | 'bundle'
  /**
   * A charge for showing up rather than for work performed: trip, dispatch,
   * after-hours, diagnostic. Membership waiver authoring targets this
   * structured type directly rather than relying on item names or tags.
   */
  | 'fee';
export type PriceType = 'flat' | 'hourly';
export type PriceStrategy = 'FLAT' | 'DYNAMIC' | 'COMPONENT_SUM';
export type LineItemStatus = 'ACTIVE' | 'ARCHIVED';

/**
 * Which item types are bought from somebody.
 *
 * Mirrors the API's `SUPPLIABLE_ITEM_TYPES`: a service is labor we perform, a
 * bundle is a way of selling other items, a membership is a plan and a fee is a
 * charge — none is purchased, so none has a supplier. Every surface that shows
 * or edits suppliers asks this, so no surface can request suppliers for an item
 * the API will refuse to attach one to.
 */
export const itemTypeCanHaveSuppliers = (type: ItemType | undefined): boolean =>
  type === 'material' || type === 'equipment';

/** Maps frontend ItemType to backend LineItemType enum value */
export const ITEM_TYPE_TO_BACKEND: Record<ItemType, string> = {
  service: 'SERVICE',
  material: 'MATERIAL',
  equipment: 'EQUIPMENT',
  membership: 'MEMBERSHIP',
  bundle: 'BUNDLE',
  fee: 'FEE',
};

/**
 * Maps backend LineItemType to frontend ItemType.
 *
 * Every backend enum member must appear here. The fallback exists only for a
 * value this build has never heard of; leaving a KNOWN type out of the map
 * silently retypes it — a `BUNDLE` read back as `service` loses its bundle
 * labels and pricing controls, and the edit form then saves the item back as a
 * SERVICE, converting the row.
 */
export function backendTypeToFrontend(backendType: string): ItemType {
  const map: Record<string, ItemType> = {
    SERVICE: 'service',
    MATERIAL: 'material',
    EQUIPMENT: 'equipment',
    MEMBERSHIP: 'membership',
    BUNDLE: 'bundle',
    FEE: 'fee',
  };
  return map[backendType.toUpperCase()] ?? 'service';
}

const PRICE_STRATEGIES: readonly PriceStrategy[] = ['FLAT', 'DYNAMIC', 'COMPONENT_SUM'];

/**
 * The stored pricing strategy, or undefined when the backend sent none.
 *
 * Read-path normalisation must not narrow the union: asserting a row into
 * `FLAT | DYNAMIC` is how a `COMPONENT_SUM` bundle comes back
 * looking flat and gets written back that way.
 */
export function parsePriceStrategy(raw: unknown): PriceStrategy | undefined {
  const value = typeof raw === 'string' ? raw.toUpperCase() : '';
  return PRICE_STRATEGIES.find((strategy) => strategy === value);
}

/**
 * The strategy an item of this type may actually be saved with.
 *
 * The two sets do not overlap: a bundle is priced from its members
 * (`COMPONENT_SUM`) or by a typed price (`FLAT`); only a service can be
 * `DYNAMIC`; everything else is flat. One function so the create and
 * edit forms cannot disagree — they did, and saving an existing bundle rewrote
 * it to `FLAT`.
 */
export function priceStrategyForItemType(
  type: ItemType | undefined | null,
  selected: PriceStrategy | undefined | null,
): PriceStrategy {
  if (type === 'bundle') return selected === 'COMPONENT_SUM' ? 'COMPONENT_SUM' : 'FLAT';
  if (type === 'service') return selected === 'DYNAMIC' ? 'DYNAMIC' : 'FLAT';
  return 'FLAT';
}

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  service: 'Service',
  material: 'Material',
  equipment: 'Equipment',
  membership: 'Membership',
  bundle: 'Bundle',
  fee: 'Fee',
};

// ─── Sub-document types ────────────────────────────────────────────────────────

export interface PricebookTag {
  id: string;
  name: string;
  color?: string | null;
  scope: string;
}

/**
 * NOT-590 — a material/equipment a SERVICE includes.
 *
 * Components are REAL priced lines: adding the parent service to an estimate
 * produces the service line plus one line per component, each with its own
 * price, tax treatment and GL mapping. Whether the CUSTOMER sees that
 * breakdown is `componentDisplayMode` — not a rendering choice only: stating
 * each part separately on the document is what takes the sale out of bundling,
 * so the mode decides taxability too.
 */
export type SupplierFreightTerms = 'PREPAID' | 'COLLECT' | 'PREPAID_AND_ADD';
export type SupplierTaxMode = 'SUPPLIER_CHARGED' | 'EXEMPT_RESALE';

/** A company we buy from. Never deleted — deactivated, so history resolves. */
export interface Supplier {
  id: string;
  name: string;
  isActive: boolean;
  accountNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
  /** Short reference, unique per company. */
  code?: string | null;
  legalName?: string | null;
  /** Visible and active, but no new order may be placed. */
  onHold?: boolean;
  holdReason?: string | null;
  /** Where purchase orders go; falls back to `email`. */
  orderEmail?: string | null;
  apEmail?: string | null;
  remitToAddress?: string | null;
  orderingAddress?: string | null;
  currency?: string | null;
  paymentTermsDays?: number | null;
  paymentTermsLabel?: string | null;
  earlyPayDiscountPercent?: number | null;
  earlyPayDiscountDays?: number | null;
  defaultLeadTimeDays?: number | null;
  minimumOrderAmount?: number | null;
  freeFreightThreshold?: number | null;
  freightTerms?: SupplierFreightTerms | null;
  defaultTaxMode?: SupplierTaxMode | null;
  allowFieldPurchases?: boolean;
  /** Business units that buy from it; empty means all. */
  businessUnitIds?: string[];
  /** Overrides the company's cost update policy; null follows it. */
  costUpdatePolicy?: SupplierCostUpdatePolicy | null;
  /** The price file columns last used for this supplier. */
  priceFileMapping?: Record<string, string> | null;
  /** Reported on the vendor's 1099 in the accounting system. */
  vendor1099?: boolean;
  /** The linked accounting vendor. */
  qbVendorId?: string | null;
  accountingSync?: {
    status?: string;
    error?: string | null;
    lastSyncedAt?: string | null;
  } | null;
}

export type SupplierCostUpdatePolicy = 'AUTO' | 'REVIEW' | 'NEVER';
export type SupplierCostSource = 'MANUAL' | 'IMPORT' | 'RECEIPT' | 'BILL' | 'API' | 'ACCOUNTING';

export type SupplierContactRole =
  | 'ORDERS'
  | 'SALES_REP'
  | 'ACCOUNTS_RECEIVABLE'
  | 'BRANCH_MANAGER'
  | 'OTHER';

export interface SupplierContact {
  id: string;
  supplierId: string;
  name: string;
  role: SupplierContactRole;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  receivesPurchaseOrders: boolean;
}

export interface SupplierBranch {
  id: string;
  supplierId: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  hours: string | null;
  acceptsWillCall: boolean;
  branchAccountNumber: string | null;
  isActive: boolean;
}

/** What one supplier charges for one catalog item. */
export interface ItemSupplier {
  id: string;
  supplierId: string;
  cost: number;
  purchaseUnit: string;
  unitsPerPurchaseUnit: number;
  supplierPartNumber?: string | null;
  /** At most one per item; its cost is the item's cost. */
  isPrimary: boolean;
  supplier?: Supplier;
  /** In purchase units. */
  minimumOrderQuantity?: number | null;
  /** Orders must be a whole multiple of this many purchase units. */
  orderMultiple?: number | null;
  leadTimeDays?: number | null;
  supplierItemDescription?: string | null;
  isDiscontinued?: boolean;
  /** What we last paid, per purchase unit. Hidden from cost-masked roles. */
  lastPaidCost?: number | null;
  lastPurchasedAt?: string | null;
  costEffectiveAt?: string | null;
  costSource?: SupplierCostSource;
}

/** One cost set or seen for an item from a supplier (PUR-CAT-6). */
export interface SupplierCostHistoryEntry {
  id: string;
  itemSupplierId: string | null;
  supplierId: string;
  supplier: { id: string; name: string };
  previousCost: number | null;
  newCost: number;
  purchaseUnit: string;
  unitsPerPurchaseUnit: number;
  source: SupplierCostSource;
  /** Whether it became the catalog cost. */
  applied: boolean;
  sourceType: string | null;
  sourceLabel: string | null;
  changedBy: string;
  changedAt: string;
}

export interface ServiceComponent {
  itemId: string;
  quantity: number;
  displayOrder?: number;
  /** Offered but unselected by default when the service lands on an estimate. */
  isOptional?: boolean;
  pricingMethod?: 'CATALOG_PRICE' | 'COST_PLUS';
  markupProfileId?: string | null;
  /**
   * The markup override for this relationship — the top rung of the markup
   * resolution order (override → the item's own → the family default).
   */
  customMarkupPercent?: number | null;
  /**
   * How THIS service charges for this part.
   *
   * The decision belongs to the relationship, not the catalog: the same water
   * heater is included in one service's price and sold beside another, so the
   * part carries no answer of its own. Absent means `separate_charge`, so every
   * component saved before this existed prices exactly as it does today.
   *
   * Either way the part contributes cost × qty × (1 + markup) to the service's
   * calculated price — this only decides whether it ALSO bills on its own row.
   */
  pricingTreatment?: PricingTreatment | null;
  /**
   * Whether the customer sees this part named on the line. It is billed either
   * way — this only controls the detail they read.
   */
  showInDetail?: boolean;
}

export interface LaborRateTier {
  fromMinute: number;
  toMinute: number | null;
  rate: number;
}

/**
 * What labor per unit of time is WORTH, tiered by how long the job runs.
 *
 * ONE number, used by both pricing modes. A dynamic service is priced from it —
 * `(hours x labor rate)` — while a flat service already has its price, so the
 * same figure lands in cost and gross margin instead. There is deliberately no
 * second "cost rate" beside it: a labor rate is what the time is worth, and the
 * mode decides what that does, not what it is.
 */
export type LaborRateProfileKind = 'HOURLY' | 'MARGIN_PERCENT';

export interface LaborRateProfile {
  id: string;
  name: string;
  /**
   * HOURLY prices time against `tiers`. MARGIN_PERCENT prices no time at all —
   * the crew takes `marginPercent` of what the service earns over its materials
   * and equipment, and `tiers` is empty.
   *
   * Absent on a profile saved before the column existed, which was hourly.
   */
  kind?: LaborRateProfileKind;
  tiers: LaborRateTier[];
  /** The share of contribution margin taken, when `kind` is MARGIN_PERCENT. */
  marginPercent?: number | null;
  isActive: boolean;
}

/** What a markup profile is for. Absent means both — see markup-profile-scope. */
export type MarkupProfileScope = 'MATERIAL' | 'EQUIPMENT' | 'BOTH';

export interface MarkupRange {
  minCost: number;
  maxCost: number | null;
  /** Always markup against cost — margin input is converted before it is sent. */
  percent: number;
  /**
   * The basis the operator typed this range in, so the editor reopens showing
   * their figure. Display only: pricing reads `percent`. Absent on anything
   * saved before the toggle existed, which means markup.
   */
  basis?: UpliftBasis;
  /**
   * What the PROFILE this range belongs to applies to — every range in one
   * profile carries the same value. It rides here because the profile row is
   * real columns and `ranges` is the only Json on it; see markup-profile-scope.
   */
  appliesTo?: MarkupProfileScope;
}

export interface MarkupProfile {
  id: string;
  name: string;
  ranges: MarkupRange[];
  isActive: boolean;
}

export interface ServiceLaborLine {
  id?: string;
  label: string;
  durationMinutes: number;
  rateSource: 'FIXED' | 'PROFILE';
  fixedRate?: number;
  laborRateProfileId?: string;
  /** Frozen rate tiers used by a materialized estimate/job service. */
  resolvedTiers?: LaborRateTier[];
  resolvedRateName?: string;
  /**
   * The margin share frozen onto a materialized estimate/job service — the twin
   * of `resolvedTiers`. A sold document keeps the percentage it was sold at,
   * whatever the profile does afterwards.
   */
  resolvedMarginPercent?: number;
}

/**
 * The margin a service earns on top of its costs, carried inside its price.
 *
 * The UI calls this "Profit uplift" and the stored shape calls it a surcharge;
 * they are one feature, the name having arrived after the fields. `appliesTo`
 * narrows what a percentage is charged on — normally labor alone, since
 * materials and equipment already earn theirs from markup profiles. A flat
 * uplift is dollars and ignores it.
 */
export interface ServiceSurchargeConfig extends ServiceSurcharge {
  /**
   * The basis the operator typed the percentage in, on the same terms as
   * `MarkupRange.basis`: `value` stays canonical markup so pricing never has to
   * ask, and this only reopens the editor showing their figure.
   */
  percentBasis?: UpliftBasis;
}

export interface DynamicServicePricingConfig {
  enabled?: boolean;
  useSameMarkupProfile?: boolean;
  materialMarkupProfileId?: string;
  equipmentMarkupProfileId?: string;
  /**
   * The family default's METHOD. "Use catalog price" is not a profile, so the
   * profile id alone cannot hold it — read as "no profile" it collapsed to
   * "Cost". Absent means COST_PLUS (against the profile, or at cost).
   */
  materialPricingMethod?: 'CATALOG_PRICE' | 'COST_PLUS';
  equipmentPricingMethod?: 'CATALOG_PRICE' | 'COST_PLUS';
  laborLines?: ServiceLaborLine[];
  complexityAdjustmentPercent?: number;
  afterHours?: { enabled: boolean; adjustmentType: 'PERCENT' | 'FLAT'; value: number };
  serviceFeeCredit?: {
    enabled: boolean;
    feeItemId?: string;
    minimumMinutes: number;
    maxCredit?: number | null;
  };
  addOnDiscount?: { enabled: boolean; discountType: 'PERCENT' | 'FLAT'; value: number };
  surcharge?: ServiceSurchargeConfig;
  /** Frozen non-part adjustment already applied when this document line was created. */
  materializedFixedAdjustment?: number;
  materializedAfterHours?: boolean;
  /**
   * The parts AS THE PRICEBOOK PRICED THEM when this service was materialized —
   * what the calculated price is actually made of.
   *
   * Snapshotted, deliberately, rather than read back off the document's part
   * lines. Those lines are the operator's to edit: repricing one to $1,000 on
   * the estimate is a fact about that line, not about the service, and reading
   * the scope panel off them made the service claim it contained a $1,000
   * filter and left the operator unable to see what the $216 was built from.
   * Only "Edit configuration" may change what this says.
   */
  materializedParts?: MaterializedServicePart[];
  /**
   * NOT A FIELD HERE: the flat pricing a document line parks while it is
   * dynamic.
   *
   * This type is the CATALOG's — `PricebookItem`, `CreateItemDto` and the
   * pricebook editors all speak it — and a catalog item has no dormant flat
   * price, because "before Dynamic" is a thing that happened on one document.
   * It lives on `DocumentDynamicPricingConfig` in `types/estimate.ts`, which
   * extends this, so the parked triple cannot be written into a catalog payload
   * by a type that cannot express it (`buildPricebookItemFromLine` strips it at
   * the one place a LINE becomes an ITEM, because types do not strip at
   * runtime).
   */
}

/** One part of a materialized dynamic service, frozen at the catalog's figures. */
export interface MaterializedServicePart {
  /** The catalog item, so the document line it was expanded into can be found. */
  itemId?: string;
  name: string;
  /** MATERIAL or EQUIPMENT — decides which base an uplift is charged on. */
  type?: string;
  quantity: number;
  /**
   * What ONE of it adds to the calculated price: cost x (1 + markup).
   *
   * The UNIT figure, so changing the configured quantity scales it the way the
   * pricebook's formula does. Its total is `unitAmount x quantity` and is
   * derived wherever it is shown rather than stored beside it, because two
   * stored numbers for one fact is how they come to disagree.
   */
  unitAmount?: number;
  /**
   * The total contribution, as the first version of this snapshot stored it.
   * Read as a fallback so a service materialized before `unitAmount` existed
   * still describes itself; never written.
   */
  amount?: number;
}

export interface DynamicPriceBreakdown {
  currentPrice: number;
  /** The complete offer total, before its amount is allocated across lines. */
  calculatedPrice: number;
  /**
   * What the catalog records this service as COSTING, and what its inputs say
   * it costs — the labor it bills plus what its parts cost us. Derived exactly
   * as the price is, and applied by the same action. Absent from older
   * backends, which never stored a cost for a dynamic service at all.
   */
  currentCost?: number;
  calculatedCost?: number;
  updateAvailable: boolean;
  laborSubtotal: number;
  /** Every part's selling contribution — all of it inside `calculatedPrice`. */
  partsSubtotal: number;
  /**
   * Of `partsSubtotal`, the amount belonging to parts that bill the customer on
   * their OWN document line. Materializing this service has to take exactly
   * this off the service line, or those parts are charged twice. Absent from
   * older backends, which charged for every part.
   */
  partsSeparatelyCharged?: number;
  complexityAdjustment: number;
  /** What the surcharge added, and what it was charged on. */
  surcharge: number;
  surchargeBase: number;
  afterHoursAdjustment: number;
  serviceFeeCredit: number;
  addOnDiscount: number;
  totalHours: number;
  laborLines: Array<{
    label: string;
    hours: number;
    amount: number;
    source: string;
    tiers: LaborRateTier[];
  }>;
  components: Array<{
    itemId: string;
    name: string;
    quantity: number;
    method: string;
    unitPrice: number;
    amount: number;
    markupPercent: number | null;
  }>;
  dependencyChanges: Array<{
    kind: 'LABOR_RATE' | 'MARKUP_PROFILE' | 'COMPONENT';
    label: string;
    before: string;
    after: string;
  }>;
}

/** How the customer sees a service's components. Presentation only. */
export type ComponentDisplayMode = 'ITEMIZED' | 'ROLLED_UP';

export const COMPONENT_DISPLAY_MODE_LABELS: Record<ComponentDisplayMode, string> = {
  ITEMIZED: 'Show each part priced',
  ROLLED_UP: 'Show one price for the whole service',
};

export interface MembershipConfig {
  planId: string | null;
  grantTierId?: string | null;
}

export interface DisplayConfig {
  hidePrice?: boolean;
  requirePhotos?: boolean;
  requireAsset?: boolean;
  /**
   * How the single customer-facing line describes itself.
   *
   * A service or bundle is always ONE line to the customer; this decides what
   * that line says underneath its name. `AUTO` builds the detail from what is
   * actually in the item — the labor and each included part — so it stays true
   * as the item is edited. `DESCRIPTION` uses the description as written.
   * `DESCRIPTION_AND_AUTO` is both, description first: the two answer different
   * questions and an operator should not have to give up one for the other.
   */
  lineDetail?: 'AUTO' | 'DESCRIPTION' | 'DESCRIPTION_AND_AUTO';
}

export function normalizeDisplayConfig(value: unknown): DisplayConfig | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const raw = value as Record<string, unknown>;
  const displayConfig: DisplayConfig = {};
  const hidePrice = raw.hidePrice;
  const requirePhotos = raw.requirePhotos;
  const requireAsset = raw.requireAsset;

  if (typeof hidePrice === 'boolean') displayConfig.hidePrice = hidePrice;
  if (typeof requirePhotos === 'boolean') displayConfig.requirePhotos = requirePhotos;
  if (typeof requireAsset === 'boolean') displayConfig.requireAsset = requireAsset;
  if (
    raw.lineDetail === 'AUTO' ||
    raw.lineDetail === 'DESCRIPTION' ||
    raw.lineDetail === 'DESCRIPTION_AND_AUTO'
  ) {
    displayConfig.lineDetail = raw.lineDetail;
  }

  return Object.keys(displayConfig).length > 0 ? displayConfig : undefined;
}

export interface UpsellRule {
  triggerType: string;
  triggerItemIds?: string[];
  suggestedItemIds: string[];
  conditionLabel: string;
  priority?: number;
}

// ─── Structured upsell items (spec v1.1 §4.6) ─────────────────────────────────

export type UpsellRelationshipType = 'upsell' | 'accessory' | 'alternative';

export interface UpsellConditions {
  assetAgeGt: number | null;
  assetCategory: string | null;
  estimateTotalGt: number | null;
  membershipStatus: string | null;
  requiresItems: string[];
}

export interface UpsellItem {
  itemId: string;
  reason: string | null;
  auto: boolean;
  aiWeight: number;
  relationshipType: UpsellRelationshipType;
  conditions: UpsellConditions;
}

export const EMPTY_UPSELL_CONDITIONS: UpsellConditions = {
  assetAgeGt: null,
  assetCategory: null,
  estimateTotalGt: null,
  membershipStatus: null,
  requiresItems: [],
};

// ─── Item (frontend display model) ────────────────────────────────────────────

/**
 * One configuration option of a configurable EQUIPMENT item — a named variant of
 * the SAME linked type, with its own price and attribute overrides.
 */
export interface PricebookConfiguration {
  name: string;
  price?: number;
  attributes?: Record<string, unknown>;
}

export interface ServicePartCostSource {
  itemId: string;
  type: string;
  costBasis: number;
  quantity: number;
  isOptional: boolean;
  /** How the service bills this part; `separate_charge` earns its own money. */
  pricingTreatment?: string | null;
  /** The unit price the relationship arrives at. */
  basePrice?: number | null;
}

export interface PricebookItem {
  // Identity
  id: string;
  name: string;
  description?: string;
  type: ItemType;
  /** Mapped from backend `code` */
  sku?: string;
  status?: LineItemStatus;
  /** READ-ONLY, derived: the units that may see this item — the union of the
   *  units on the folders it is filed in. Empty = every unit. Never sent on a
   *  write; a unit attaches to the catalog at the folder only. */
  businessUnitIds?: string[];

  // Pricing
  /** Mapped from backend `basePrice` */
  price: number;
  priceType: PriceType;
  priceStrategy?: PriceStrategy;
  /**
   * The `priceStrategy` EXACTLY as stored, before `parsePriceStrategy` narrowed
   * it to the union above.
   *
   * They differ only when a row holds a value the enum does not define, which
   * a DTO would have rejected but a direct-to-DB import can still write —
   * production carries `COST_PLUS` rows from exactly that path. `priceStrategy`
   * defaults such a row to `FLAT` so the UI stays total; this field keeps the
   * original token so a faithful reader (the export) can decline to repeat that
   * assumption, and so the bad rows are findable at all.
   */
  priceStrategyRaw?: string;
  /** SERVICE — the hourly BILL rate. Prices a DYNAMIC service; never a cost. */
  laborRate?: number;
  /**
   * SERVICE — what an hour of this work COSTS us.
   *
   * The other half of the pair, and the only one costing reads. Applies in BOTH
   * modes: a FLAT service's hours are cost-only, and a dynamic service earns its
   * margin on labor from the gap between `laborRate` and this.
   */
  laborCostRate?: number;
  /** SERVICE — default hours the work takes. Cost on a flat service; price on a dynamic one. */
  estimatedHours?: number;
  /**
   * SERVICE (FLAT) — the labor rows the hours are made of: duration, rate type
   * and rate, one per crew. The EDITOR's form of the fact `estimatedHours` and
   * `laborCostRate` hold reduced; costing reads the reduced pair, so both are
   * always written together.
   */
  laborCostLines?: ServiceLaborLine[];
  dynamicPricingConfig?: DynamicServicePricingConfig;
  /** Mapped from backend `costBasis` */
  cost?: number;
  markupPercent?: number;
  /*
   * No `chargeable` on a catalog item: whether a part bills on a line of its
   * own is a fact about the SALE, held by the service component's
   * `pricingTreatment` and by the document line's own flag.
   */
  /**
   * Whether the item appears on the customer's document by default — the
   * catalog's answer, which the document line may overrule. Absent means
   * VISIBLE (backend `@default(true)`).
   *
   * Independent of what the item is billed at, in both directions: an item
   * billed but hidden still counts in the subtotal, the tax and the total, and
   * one shown but not billed prints as "Included" with no amount.
   */
  visibleToCustomer?: boolean;
  // NOT-605 member pricing (per-item override)
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  currency?: string;
  unit?: string;

  // Tax
  taxable?: boolean;
  taxCategory?: string | null;
  taxCode?: string | null;
  taxLineType?: RuntimeTaxLineType | null;

  // GL / Display
  glAccount?: string | null;
  glClass?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  displayOrder?: number;
  displayConfig?: DisplayConfig;

  // NOT-590 — materials/equipment a SERVICE includes, and how the customer
  // sees them. Empty for every other type.
  components?: ServiceComponent[];
  /**
   * A SERVICE's parts reduced to what their cost is computed from — carried
   * on LIST rows so the grid composes the service's cost (labor rows + parts)
   * with the same helper the detail page uses, instead of reading the row's
   * `costBasis`, which only the dynamic apply-price ever writes.
   */
  partCostSources?: ServicePartCostSource[];
  componentDisplayMode?: ComponentDisplayMode;

  // Membership
  membershipConfig?: MembershipConfig;
  membershipPlanIds?: string[];

  // Upsell
  /** @deprecated legacy model — new reads/writes use `upsellItems` */
  upsellRules?: UpsellRule[];
  upsellItems?: UpsellItem[];

  // AI & Search
  aiTags?: string[];
  requiredAssets?: string[];
  hazardLevel?: number;
  retrievalBoost?: number;

  // Equipment (type === 'equipment'): the EquipmentDescription spec this sells.
  equipmentDescriptionId?: string | null;
  // Equipment (type === 'equipment'): configurable placeholder configured on top
  // of its linked type; the operator picks one of `configurations` at estimate
  // time. Each option is a named variant of the SAME base type.
  isConfigurable?: boolean;
  configurations?: PricebookConfiguration[];

  // Image & tags
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  tags?: PricebookTag[];

  // Browse folders this item is filed in. One tree covers the whole catalog,
  // so this is independent of `type`. Primary first.
  categories?: ItemCategoryMembership[];

  // Versioning (backend increments on edit)
  itemVersion?: number;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/** One parent that includes this item, and how many of it that parent takes. */
export interface PricebookParentService {
  id: string;
  name: string;
  code: string;
  /**
   * SERVICE or BUNDLE. Optional on purpose: a backend that predates the type
   * being returned is a row this UI still has to render, and typing it as
   * required is what let `type.toUpperCase()` reach a render path — which
   * throws, and takes the whole detail page with it rather than one label.
   */
  type?: string;
  quantity: number;
}

/** Where-used counts for an item (NOT-103). */
export interface PricebookItemUsage {
  /** How many SERVICES include this item as a component — services only. */
  inServices: number;
  /** How many BUNDLES include it. A bundle carries components too. */
  inBundles?: number;
  /** Every parent whatever its type: the true size of `parents`. */
  inParents?: number;
  usedOnEstimates: number;
  usedOnJobs: number;
  /**
   * WHICH parents include it, capped server-side. `inParents` remains the true
   * total, so a longer list is possible; the two disagreeing is the signal that
   * the list was cut, not that a count is wrong. The type BREAKDOWN comes from
   * the counts rather than from these rows, so a bundle hidden past the cap
   * cannot make the summary wording claim they are all services.
   */
  parents?: PricebookParentService[];
  /**
   * The pre-rename key. A frontend deploys ahead of its backend often enough
   * that reading only the new name shows an empty card for the gap between
   * them — the data is there, under the name it used to have.
   *
   * @deprecated Read `parents`; this is the compatibility fallback.
   */
  parentServices?: PricebookParentService[];
}

// ─── Categories (NOT-590) ─────────────────────────────────────────────────────

/**
 * Pricebook categories are folders for BROWSING the catalog — navigation and
 * catalog management only.
 *
 * They deliberately carry no money: no price, no GL account, no revenue
 * rollup. Notifi already answers "where did this revenue come from" with
 * business unit, job type and item type; a category-based rollup would be a
 * fourth competing answer. If someone asks for "revenue by category", the
 * answer is one of those three axes — this model will not grow one.
 *
 * ONE tree for the whole catalog. A folder holds services, materials and
 * equipment side by side: "Filters" is one place to look, not three. A category
 * never discriminates on item type — the item's own `type` already does.
 */

/** How the items inside a folder are ordered. */
export type CategoryItemSortMode = 'MANUAL' | 'USAGE' | 'ALPHA';

export interface PricebookCategory {
  id: string;
  name: string;
  description?: string | null;
  parentId?: string | null;
  displayOrder: number;
  icon?: string | null;
  imageUrl?: string | null;
  /**
   * BROWSE VISIBILITY ONLY. Empty = visible to everyone. Non-empty = the folder
   * appears only for users whose own BU membership intersects it.
   *
   * Never revenue attribution, GL posting or any export. Item-level scoping
   * stays authoritative for availability — an item the user may use stays
   * findable by search even when a containing folder is hidden from them.
   */
  businessUnitIds: string[];
  /** Hides the folder from the tech app entirely (office-only categories). */
  hideInMobile: boolean;
  /** Empty = every role sees the folder. */
  visibleToRoles: string[];
  itemSortMode: CategoryItemSortMode;
  /**
   * Which family's tree this folder belongs to. `null` is a legacy any-kind
   * folder, which appears in EVERY family's tree — so a row's type cannot be
   * inferred from the tree you happened to open it from.
   */
  itemFamily?: 'SERVICE' | 'MATERIAL' | 'EQUIPMENT' | null;
  /** ISO timestamp of the last change to the folder itself. */
  updatedAt?: string;
  /** Distinct non-deleted items filed directly in this folder. */
  itemCount: number;
  /** `itemCount` plus every descendant's, each item still counted once. */
  totalItemCount: number;
}

export interface PricebookCategoryNode extends PricebookCategory {
  children: PricebookCategoryNode[];
}

export interface PricebookCategoryRow extends PricebookCategory {
  /** Depth from the root, 0-based. */
  depth: number;
}

export interface ItemCategoryMembership {
  categoryId: string;
  name: string;
  isPrimary: boolean;
  displayOrder: number;
}

export interface CreateCategoryDto {
  /**
   * Which family's tree this folder belongs to. Only meaningful for a root
   * folder — a child inherits its parent's.
   */
  itemFamily?: 'SERVICE' | 'MATERIAL' | 'EQUIPMENT';

  name: string;
  description?: string | null;
  parentId?: string | null;
  icon?: string | null;
  imageUrl?: string | null;
  displayOrder?: number;
  itemSortMode?: CategoryItemSortMode;
}

/**
 * Rename/re-icon/re-order. Deliberately cannot carry scoping — that is a
 * separate owner/admin-only, audit-logged call.
 */
export type UpdateCategoryDto = Partial<Omit<CreateCategoryDto, 'parentId'>>;

/**
 * Re-parent one folder. Separate from the update DTO on purpose: a rename must
 * not be able to move a folder by accident, and the server validates the move
 * (cycle, depth, name clash in the destination) before it writes.
 */
export interface MoveCategoryDto {
  /** `null` moves the folder to the top level. */
  parentId?: string | null;
  displayOrder?: number;
}

export interface UpdateCategoryScopeDto {
  businessUnitIds?: string[];
  hideInMobile?: boolean;
  visibleToRoles?: string[];
}

export interface CategoryPosition {
  id: string;
  parentId?: string | null;
  displayOrder: number;
}

/**
 * Bulk edit "via category": applies to every member ITEM. Note what is absent —
 * business unit. GL and BU answer different questions and neither may default
 * the other, so BU is not bulk-editable from a folder.
 */
export interface BulkEditCategoryItemsDto {
  glAccount?: string | null;
  glClass?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  taxable?: boolean;
}

/** A catalog item as listed inside a category folder. */
export interface CategoryItemRow {
  id: string;
  name: string;
  code: string;
  type: string;
  /** FLAT or DYNAMIC — what decides whether the price above was typed. */
  priceStrategy?: string;
  status: string;
  basePrice: number;
  unit: string;
  imageUrl?: string | null;
  businessUnitIds: string[];
  usageCount: number;
  componentDisplayMode: ComponentDisplayMode;
  categoryId: string;
  isPrimary: boolean;
  displayOrder: number;
}

// ─── DTOs (backend-compatible field names) ────────────────────────────────────

export interface CreateItemDto {
  // Identity
  name: string;
  code: string;
  description?: string;
  type: ItemType;
  status?: LineItemStatus;
  // Pricing
  basePrice?: number;
  laborRate?: number;
  /** See `PricebookItem.laborCostRate`. Cost only — never moves a price. */
  laborCostRate?: number;
  /** See `PricebookItem.estimatedHours`. */
  estimatedHours?: number;
  /** See `PricebookItem.laborCostLines`. */
  laborCostLines?: ServiceLaborLine[];
  dynamicPricingConfig?: DynamicServicePricingConfig;
  costBasis?: number;
  markupPercent?: number;
  /** See `PricebookItem.visibleToCustomer`. Absent means visible. */
  visibleToCustomer?: boolean;
  // NOT-605 member pricing (per-item override)
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  currency?: string;
  priceStrategy?: PriceStrategy;
  unit?: string;
  // Tax
  taxable?: boolean;
  taxCategory?: string;
  taxCode?: string;
  taxLineType?: RuntimeTaxLineType;
  // Display / GL
  glAccount?: string;
  glClass?: string;
  glCogs?: string;
  glExpenseAccount?: string;
  displayOrder?: number;
  displayConfig?: DisplayConfig;
  // Service components (SERVICE only)
  components?: ServiceComponent[];
  componentDisplayMode?: ComponentDisplayMode;
  // Membership (MEMBERSHIP only)
  membershipPlanIds?: string[];
  membershipConfig?: MembershipConfig;
  // Upsell
  upsellRules?: UpsellRule[];
  upsellItems?: UpsellItem[];
  // AI & metadata
  aiTags?: string[];
  requiredAssets?: string[];
  hazardLevel?: number;
  retrievalBoost?: number;
  // Equipment (type === 'equipment')
  equipmentDescriptionId?: string;
  isConfigurable?: boolean;
  configurations?: PricebookConfiguration[];
  // Image & tags
  imageUrl?: string;
  tagIds?: string[];
  /**
   * Browse folders to file this item into. On update an empty array clears the
   * memberships and an absent key leaves them alone, so a save from a surface
   * that never showed the picker cannot silently unfile the item.
   */
  categoryIds?: string[];
  /** Must be one of `categoryIds`. Defaults to the first when omitted. */
  primaryCategoryId?: string;
}

export interface UpdateItemDto extends Omit<
  Partial<CreateItemDto>,
  | 'equipmentDescriptionId'
  | 'taxCategory'
  | 'taxCode'
  | 'taxLineType'
  | 'glAccount'
  | 'glClass'
  | 'glCogs'
  | 'glExpenseAccount'
  | 'imageUrl'
> {
  id: string;
  equipmentDescriptionId?: string | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  taxLineType?: RuntimeTaxLineType | null;
  glAccount?: string | null;
  glClass?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  imageUrl?: string | null;
}

export interface ImageUploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface ImageUploadUrlResponse {
  uploadUrl: string;
  attachment: {
    id: string;
    fileKey: string;
    fileName: string;
    mimeType: string;
  };
}

// ─── List ─────────────────────────────────────────────────────────────────────

export interface PricebookListParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: ItemType | '';
  /** Exact accounting category, including legacy cross-type classifications. */
  taxCategory?: string;
  /** Exact status match; pair `ARCHIVED` with `includeArchived` or it matches nothing. */
  status?: 'ACTIVE' | 'ARCHIVED';
  /** Lift the server's soft-delete filter. Needs company:edit. */
  includeArchived?: boolean;
  tagIds?: string[];
  ids?: string[];
  /** Server-side browse-folder filter; includes subfolders unless disabled. */
  categoryId?: string;
  includeSubcategories?: boolean;
  /** Column sort, applied in SQL so it orders the catalog and not one page. */
  sortBy?: 'name' | 'code' | 'basePrice' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
  /**
   * Grid column filters, applied server-side for the same reason as the sort.
   * COST is deliberately absent: a service's cost is composed from its labor
   * rows and parts rather than stored, so it cannot be a SQL predicate — the
   * grid loads the whole catalog when that filter is used.
   */
  code?: string;
  unit?: string;
  glAccount?: string;
  taxable?: boolean;
  priceOp?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  priceValue?: number;
  priceStrategy?: PriceStrategy;
  /** Everything except this strategy — the grid's "Flat" is "not DYNAMIC". */
  priceStrategyNot?: PriceStrategy;
  businessUnitId?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
}

export interface PricebookListResponse {
  items: PricebookItem[];
  total: number;
  page: number;
  limit: number;
}

// ─── Import ───────────────────────────────────────────────────────────────────

export interface ImportItemsDto {
  items: CreateItemDto[];
}

export interface ImportItemsResponse {
  created: number;
  updated: number;
  failed: number;
  duplicateCodes: string[];
  errors?: string[];
}
